'use client';
import { useEffect, useRef, useState } from 'react';

import type { BannerState } from '~features/retentionBanners/model/types';
import { useSession } from '~hooks/useSession';
import { userApi } from '~services/api/user';
import { retentionApi } from '~shared/api/retention';
import type { RetentionBannerPlacement } from '~shared/api/retention/types';

export const useGetRetentionBanner = (placement: RetentionBannerPlacement) => {
	const { userId, email } = useSession();

	const requestInitiated = useRef(false);
	const [bannerStatus, setBannerStatus] = useState<BannerState>({
		status: 'idle',
	});
	const { data: userConfigData } = userApi.useUserConfigQuery(
		{ userId },
		{ skip: !userId }
	);

	const [getBanners, { data, error, isLoading }] =
		retentionApi.useBannersConfigMutation({
			fixedCacheKey: `getBanner${placement}`,
		});
	useEffect(() => {
		let callbackId: number | ReturnType<typeof setTimeout> | undefined;
		if (
			userConfigData?.retentionJwt &&
			userId &&
			!requestInitiated.current &&
			bannerStatus.status === 'idle'
		) {
			requestInitiated.current = true;
			const command =
				typeof requestIdleCallback === 'function'
					? requestIdleCallback
					: setTimeout;
			callbackId = command(() => {
				if (!userConfigData?.retentionJwt || !userId) {
					requestInitiated.current = false;
					return;
				}
				void getBanners({
					userId,
					email,
					jwt: userConfigData.retentionJwt,
					placement,
				});
			});
		}
		return () => {
			if (
				typeof requestIdleCallback === 'function' &&
				typeof callbackId === 'number'
			) {
				cancelIdleCallback(callbackId);
			} else {
				clearTimeout(callbackId);
			}
			requestInitiated.current = false;
		};
	}, [bannerStatus, getBanners, userConfigData, userId, email, placement]);

	useEffect(() => {
		if (data?.status === 'inprogress') {
			setBannerStatus({ status: 'generating' });
		} else if (
			data?.status === 'ready' &&
			data?.result.banners.length > 0 &&
			data?.result.placement === placement
		) {
			setBannerStatus({
				status: 'ready',
				banners: data.result.banners.map((banner) => {
					return {
						content: banner.content,
						id: banner.id,
					};
				}),
			});
		} else if (error && (error as { status: number }).status === 404) {
			setBannerStatus({ status: 'no-banner-available' });
		} else if (isLoading) {
			setBannerStatus({ status: 'loading' });
		} else if (error) {
			setBannerStatus({ status: 'error' });
		} else {
			setBannerStatus({ status: 'idle' });
		}
	}, [data, isLoading, error, placement]);

	return bannerStatus;
};
